import { useEffect, useState } from "react";
import axios from "axios";
import { AFF_API, getAffHeaders } from "../../context/AffAuthContext";
import { toast } from "sonner";
import { Radio, Lock, Sparkle } from "@phosphor-icons/react";
import { Wifi, WifiOff, CheckCircle2, AlertTriangle } from "lucide-react";

/** Affiliate-side: enable copy trading on the primary CENT account.
 *  Three lot modes — Fixed lot, % of master, Risk %.
 *  USD shows a "Coming Soon" tile below. */
export default function BridgeControls({ accounts }) {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const s = await axios.get(`${AFF_API}/affiliate/bridge/status`, { headers: getAffHeaders() });
      setData(s.data);
      if (!draft) setDraft(s.data.bridge);
    } catch (err) { /* ignore */ }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const save = async (overrideEnabled) => {
    if (!draft) return;
    setBusy(true);
    try {
      const payload = {
        enabled: overrideEnabled !== undefined ? overrideEnabled : draft.enabled,
        lot_mode: draft.lot_mode,
        fixed_lot: draft.fixed_lot,
        pct_of_master: draft.pct_of_master,
        risk_pct: draft.risk_pct,
      };
      const r = await axios.put(`${AFF_API}/affiliate/bridge/cent`, payload, { headers: getAffHeaders() });
      setDraft(r.data.bridge);
      toast.success(r.data?.message || "Saved");
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  if (!data || !draft) {
    return (
      <div className="rounded-xl border border-zinc-800/50 bg-zinc-900/60 p-5 animate-pulse" data-testid="bridge-card-loading">
        <div className="h-4 w-32 bg-zinc-800 rounded mb-2" />
        <div className="h-3 w-64 bg-zinc-800 rounded" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="bridge-section">
      {/* CENT bridge card */}
      <div className="rounded-xl border border-blue-500/20 bg-gradient-to-br from-blue-500/5 to-zinc-900/80 backdrop-blur-md p-5" data-testid="bridge-card-cent">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <Radio size={18} weight="duotone" className="text-blue-400" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-zinc-50" style={{ fontFamily: "Manrope" }}>MT5 Bridge — Copy Trading</h3>
              <p className="text-[10px] text-zinc-500">CENT account · auto-mirrors Velox master signals</p>
            </div>
          </div>
          <Toggle
            on={draft.enabled}
            testid="bridge-toggle"
            disabled={busy || !data.eligible_cent || !data.master_configured}
            onChange={(v) => { setDraft(d => ({ ...d, enabled: v })); save(v); }}
          />
        </div>

        {/* Blockers */}
        {!data.eligible_cent && (
          <Notice testid="bridge-blocker-cent" tone="amber" icon={Lock}>
            Activate your CENT MT5 subscription to enable copy trading.
          </Notice>
        )}
        {data.eligible_cent && !data.master_configured && (
          <Notice testid="bridge-blocker-master" tone="amber" icon={AlertTriangle}>
            Velox is preparing the master signal feed. You'll be able to enable copy trading shortly.
          </Notice>
        )}
        {data.stub_mode && data.master_configured && (
          <Notice testid="bridge-stub-notice" tone="amber" icon={AlertTriangle}>
            Bridge is running in test mode. Trades will resume once the live signal feed is connected.
          </Notice>
        )}

        {/* Lot mode selector */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          {data.lot_modes.map((m) => (
            <button
              key={m}
              data-testid={`bridge-mode-${m}`}
              disabled={busy}
              onClick={() => setDraft(d => ({ ...d, lot_mode: m }))}
              className={`p-2.5 rounded-md text-[10px] font-semibold border transition-all uppercase tracking-wider ${
                draft.lot_mode === m
                  ? "bg-blue-500/15 border-blue-500/40 text-blue-300"
                  : "bg-zinc-950/40 border-zinc-800 text-zinc-500 hover:border-zinc-700"
              }`}
            >
              {labelFor(m)}
            </button>
          ))}
        </div>

        {/* Per-mode input */}
        {draft.lot_mode === "fixed_lot" && (
          <LotInput
            testid="bridge-fixed-lot"
            label="Lot size per trade"
            suffix="lots"
            value={draft.fixed_lot}
            step={0.01}
            min={0.01}
            max={100}
            onChange={(v) => setDraft(d => ({ ...d, fixed_lot: v }))}
            hint="Same lot used on every copied trade, regardless of master volume."
          />
        )}
        {draft.lot_mode === "pct_master" && (
          <LotInput
            testid="bridge-pct-master"
            label="Percentage of master lot"
            suffix="%"
            value={draft.pct_of_master}
            step={1}
            min={1}
            max={1000}
            onChange={(v) => setDraft(d => ({ ...d, pct_of_master: v }))}
            hint={`If master trades 1.00 lot, you trade ${((draft.pct_of_master || 0) / 100).toFixed(2)} lot.`}
          />
        )}
        {draft.lot_mode === "risk_pct" && (
          <LotInput
            testid="bridge-risk-pct"
            label="Risk per trade"
            suffix="% of equity"
            value={draft.risk_pct}
            step={0.1}
            min={0.1}
            max={50}
            onChange={(v) => setDraft(d => ({ ...d, risk_pct: v }))}
            hint="Position size adapts to your account equity. Higher = more risk."
          />
        )}

        {/* Save row */}
        <div className="flex items-center justify-between mt-4 flex-wrap gap-2">
          <div className="text-[10px] text-zinc-500">
            {draft.last_synced_at ? (
              <span className="inline-flex items-center gap-1 text-emerald-400">
                <CheckCircle2 size={11} className="text-emerald-400" /> Last synced {draft.last_synced_at.slice(0, 16)}
              </span>
            ) : draft.enabled ? (
              <span className="inline-flex items-center gap-1 text-amber-300">
                <Wifi size={11} /> Provisioning — first trade will arrive shortly
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-zinc-500">
                <WifiOff size={11} /> Not subscribed
              </span>
            )}
          </div>
          <button
            data-testid="bridge-save"
            onClick={() => save()}
            disabled={busy}
            className="px-3 py-1.5 rounded-md text-[11px] font-semibold bg-blue-500/15 hover:bg-blue-500/25 border border-blue-500/30 text-blue-200 disabled:opacity-40"
          >
            {busy ? "Saving…" : "Save Settings"}
          </button>
        </div>

        {draft.last_error && (
          <p data-testid="bridge-last-error" className="mt-3 p-2.5 rounded bg-rose-500/10 border border-rose-500/30 text-[10px] text-rose-300 leading-relaxed">
            <AlertTriangle size={10} className="inline mr-1" />
            <span className="font-semibold">Copy attempt failed:</span> {draft.last_error}
            {String(draft.last_error).toLowerCase().includes("server") && (
              <span className="block mt-1 text-rose-200/80">
                ↳ Your MT5 server name looks wrong. Open MT5 desktop → File → Login to Trade Account → check the exact server name in the dropdown, then update it in the MT5 Accounts section above.
              </span>
            )}
            {(String(draft.last_error).toLowerCase().includes("invalid account") || String(draft.last_error).toLowerCase().includes("authenticate")) && (
              <span className="block mt-1 text-rose-200/80">
                ↳ Your broker rejected the credentials. Verify your MT5 login number and that you're using the <b>Main (trading)</b> password, not Investor.
              </span>
            )}
          </p>
        )}
      </div>

      {/* USD coming soon */}
      <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4" data-testid="bridge-card-usd-soon">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
            <Sparkle size={14} weight="duotone" className="text-amber-300" />
          </div>
          <div>
            <h4 className="text-[12px] font-bold text-amber-100">USD Bridge — Coming Soon</h4>
            <p className="text-[10px] text-amber-200/70">Copy trading on USD accounts will be available in the next release. Your CENT account is already supported.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function labelFor(m) {
  if (m === "fixed_lot") return "Fixed lot";
  if (m === "pct_master") return "% of master";
  if (m === "risk_pct") return "Risk %";
  return m;
}

function Toggle({ on, onChange, disabled, testid }) {
  return (
    <button
      data-testid={testid}
      type="button"
      disabled={disabled}
      onClick={() => !disabled && onChange(!on)}
      className={`relative w-11 h-6 rounded-full transition-colors ${on ? "bg-blue-500" : "bg-zinc-700"} disabled:opacity-40 disabled:cursor-not-allowed`}
    >
      <span className={`absolute top-0.5 ${on ? "left-5" : "left-0.5"} w-5 h-5 rounded-full bg-white transition-all`} />
    </button>
  );
}

function LotInput({ label, value, onChange, suffix, step, min, max, hint, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">{label}</label>
      <div className="flex items-center gap-2 mt-1">
        <input
          data-testid={testid}
          type="number"
          step={step}
          min={min}
          max={max}
          value={value ?? ""}
          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          className="flex-1 px-3 py-2 rounded-md bg-zinc-950 border border-zinc-800 text-white text-sm font-mono outline-none focus:border-blue-500/50"
        />
        <span className="text-[11px] text-zinc-500 min-w-[80px]">{suffix}</span>
      </div>
      {hint && <p className="text-[10px] text-zinc-500 mt-1.5">{hint}</p>}
    </div>
  );
}

function Notice({ children, tone = "amber", icon: Icon, testid }) {
  const palette = tone === "amber"
    ? "bg-amber-500/5 border-amber-500/30 text-amber-200"
    : "bg-emerald-500/5 border-emerald-500/30 text-emerald-200";
  return (
    <div data-testid={testid} className={`mb-3 p-2.5 rounded-md border text-[11px] flex items-start gap-2 ${palette}`}>
      {Icon && <Icon size={12} className="mt-0.5 flex-shrink-0" />}
      <span>{children}</span>
    </div>
  );
}
